import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Box, Typography, Button } from '@mui/material'
import { setDecimalPrecision } from 'utils/helpers'
import type { InvoicePaymentCreation } from 'components/containers/invoice-payments/types'
import { distributePaymentProportionally } from '../../utils/utils'

interface DistributeButtonProps {
  selectedInvoices: InvoicePaymentCreation[]
}

export const DistributeButton: React.FC<DistributeButtonProps> = ({ selectedInvoices }) => {
  const { control, setValue } = useFormContext()

  const paymentAmount = useWatch({
    control,
    name: 'paymentAmount',
    defaultValue: '',
  })
  const invoicesFormData = useWatch({
    control,
    name: 'invoices',
    defaultValue: {},
  })

  const { isDistributed, distributedInvoices } = useMemo(() => {
    const distributedPayments = distributePaymentProportionally(
      Number(paymentAmount),
      selectedInvoices,
      invoicesFormData,
    )

    const distributedInvoices = { ...invoicesFormData }

    distributedPayments.forEach(payment => {
      distributedInvoices[payment.id] = {
        ...distributedInvoices[payment.id],
        amountPaid: setDecimalPrecision(payment.amountPaid, {
          decimalPrecisionOverride: 2,
          shouldFormatNumber: false,
        }),
        adjustment: distributedInvoices[payment.id]?.adjustment || 0,
      }
    })

    const isDistributed = selectedInvoices.every(invoice => {
      return invoicesFormData?.[invoice.id]?.amountPaid === distributedInvoices?.[invoice.id]?.amountPaid
    })

    return {
      isDistributed,
      distributedInvoices,
    }
  }, [paymentAmount, invoicesFormData, selectedInvoices])

  if (selectedInvoices.length === 0) return null

  return (
    <Box display="flex" alignItems="center" data-testid="distribute-button">
      <Typography variant="body2" mr={2} data-testid="selected-count">
        {selectedInvoices.length} selected
      </Typography>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={() => {
          setValue('invoices', distributedInvoices)
        }}
        disabled={isDistributed}
        data-testid="distribute-payment-button"
      >
        Distribute Payment Proportionally
      </Button>
    </Box>
  )
}

export default DistributeButton
